import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assetsApi,
  authApi,
  bookVersionsApi,
  charactersApi,
  configurationsApi,
  generationRunsApi,
  healthApi,
  jobsApi,
  storyPlansApi,
  studioApi,
  templatesApi,
  uploadsApi,
} from "./client";
import type {
  AutosaveDraftRequest,
  ConfirmUploadRequest,
  CreateConfigurationRequest,
  CreateSessionRequest,
  LoginRequest,
  PresignUploadRequest,
  RegisterRequest,
  StartConfigurationRequest,
  UpdateConfigurationRequest,
} from "./types";

// Query key factory keeps keys consistent and refactor-safe.
export const queryKeys = {
  health: ["health"] as const,
  me: ["auth", "me"] as const,
  authConfig: ["auth-config"] as const,
  studio: (id: string) => ["studio", id] as const,
  templates: (params?: {
    page?: number;
    page_size?: number;
    category?: string;
    language?: string;
  }) => ["templates", "list", params ?? {}] as const,
  template: (id: string) => ["templates", "detail", id] as const,
  templateVersions: (id: string, params?: PageParams) =>
    ["templates", id, "versions", params ?? {}] as const,
  templateVersion: (id: string, version: number) =>
    ["templates", id, "versions", version] as const,
  configurations: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }) => ["configurations", "list", params ?? {}] as const,
  configuration: (id: string) => ["configurations", "detail", id] as const,
  configurationVersions: (id: string, params?: PageParams) =>
    ["configurations", id, "versions", params ?? {}] as const,
  configurationVersion: (id: string, version: number) =>
    ["configurations", id, "versions", version] as const,
  latestDraft: (id: string) => ["configurations", id, "draft"] as const,
  characterProfiles: (configurationId: string) =>
    ["configurations", configurationId, "character-profiles"] as const,
  characterAnchors: (profileId: string) =>
    ["character-profiles", profileId, "anchors"] as const,
  storyPlans: (configurationId: string) =>
    ["configurations", configurationId, "story-plans"] as const,
  storyPlan: (id: string) => ["story-plans", id] as const,
  storyPlanFull: (id: string) => ["story-plans", id, "full"] as const,
  scenePlan: (id: string, page: number) =>
    ["story-plans", id, "scenes", page] as const,
  generationRuns: (
    configurationId: string,
    params?: { status?: string; run_type?: string },
  ) =>
    [
      "configurations",
      configurationId,
      "generation-runs",
      params ?? {},
    ] as const,
  generationRun: (id: string) => ["generation-runs", id] as const,
  candidates: (runId: string) =>
    ["generation-runs", runId, "candidates"] as const,
  asset: (id: string) => ["assets", id] as const,
  bookVersions: (configurationId: string) =>
    ["configurations", configurationId, "book-versions"] as const,
  bookVersion: (id: string) => ["book-versions", id] as const,
  renderedAssets: (id: string) =>
    ["book-versions", id, "rendered-assets"] as const,
  jobs: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    job_type?: string;
  }) => ["jobs", "list", params ?? {}] as const,
  job: (id: string) => ["jobs", id] as const,
  configurationJobs: (id: string) => ["configurations", id, "jobs"] as const,
};

type PageParams = { page?: number; page_size?: number };

// --- Health ---
export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => healthApi.check(),
    staleTime: 30_000,
  });
}

// --- Auth ---
export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSessionRequest) =>
      authApi.createSession(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: () => authApi.config(),
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (payload: LoginRequest) => authApi.login(payload),
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (payload: RegisterRequest) => authApi.register(payload),
  });
}

// --- Studio ---
export function useStudio(configurationId: string) {
  return useQuery({
    queryKey: queryKeys.studio(configurationId),
    queryFn: ({ signal }) => studioApi.get(configurationId, signal),
    enabled: !!configurationId,
    // Poll while the worker is busy; signed image URLs live for one hour.
    refetchInterval: (query) =>
      (query.state.data?.active_jobs ?? 0) > 0 ? 3_000 : 5 * 60_000,
  });
}

export function useStudioAction<TArgs>(
  configurationId: string,
  action: (args: TArgs) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.studio(configurationId),
      });
    },
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });
}

// --- Templates ---
export function useTemplates(
  params: PageParams & { category?: string; language?: string } = {},
) {
  return useQuery({
    queryKey: queryKeys.templates(params),
    queryFn: () => templatesApi.list(params),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: queryKeys.template(id),
    queryFn: () => templatesApi.get(id),
    enabled: !!id,
  });
}

export function useTemplateVersions(id: string, params: PageParams = {}) {
  return useQuery({
    queryKey: queryKeys.templateVersions(id, params),
    queryFn: () => templatesApi.listVersions(id, params),
    enabled: !!id,
  });
}

// --- Configurations ---
export function useConfigurations(
  params: PageParams & { status?: string } = {},
) {
  return useQuery({
    queryKey: queryKeys.configurations(params),
    queryFn: () => configurationsApi.list(params),
  });
}

export function useConfiguration(id: string) {
  return useQuery({
    queryKey: queryKeys.configuration(id),
    queryFn: () => configurationsApi.get(id),
    enabled: !!id,
  });
}

export function useConfigurationVersions(id: string, params: PageParams = {}) {
  return useQuery({
    queryKey: queryKeys.configurationVersions(id, params),
    queryFn: () => configurationsApi.listVersions(id, params),
    enabled: !!id,
  });
}

export function useConfigurationVersion(id: string, version: number) {
  return useQuery({
    queryKey: queryKeys.configurationVersion(id, version),
    queryFn: () => configurationsApi.getVersion(id, version),
    enabled: !!id,
  });
}

export function useCreateConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateConfigurationRequest) =>
      configurationsApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
}

export function useUpdateConfiguration(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateConfigurationRequest) =>
      configurationsApi.update(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.configuration(id),
      });
      void queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
}

export function useArchiveConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configurationsApi.archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
}

export function useLatestDraft(id: string) {
  return useQuery({
    queryKey: queryKeys.latestDraft(id),
    queryFn: () => configurationsApi.getLatestDraft(id),
    enabled: !!id,
  });
}

export function useStartConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StartConfigurationRequest) =>
      configurationsApi.start(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
}

export function useAutosaveDraft(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AutosaveDraftRequest) =>
      configurationsApi.autosaveDraft(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.latestDraft(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.configuration(id),
      });
    },
  });
}

// --- Uploads ---
export function usePresignUpload() {
  return useMutation({
    mutationFn: (payload: PresignUploadRequest) => uploadsApi.presign(payload),
  });
}

export function useConfirmUpload() {
  return useMutation({
    mutationFn: (payload: ConfirmUploadRequest) => uploadsApi.confirm(payload),
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => assetsApi.delete(assetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// --- Characters ---
export function useCharacterProfiles(configurationId: string) {
  return useQuery({
    queryKey: queryKeys.characterProfiles(configurationId),
    queryFn: () => charactersApi.listProfiles(configurationId),
    enabled: !!configurationId,
  });
}

export function useCharacterAnchors(profileId: string) {
  return useQuery({
    queryKey: queryKeys.characterAnchors(profileId),
    queryFn: () => charactersApi.listAnchors(profileId),
    enabled: !!profileId,
  });
}

export function useAnchorGateStatus(profileId: string) {
  return useQuery({
    queryKey: ["character-profiles", profileId, "anchor-gate"] as const,
    queryFn: () => charactersApi.getAnchorGateStatus(profileId),
    enabled: !!profileId,
  });
}

export function useApproveCharacterProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => charactersApi.approveProfile(profileId),
    onSuccess: (_data, profileId) => {
      void queryClient.invalidateQueries({
        queryKey: ["character-profiles", profileId],
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.characterProfiles(""),
      });
    },
  });
}

export function useGenerateAnchorPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      profileId,
      payload,
    }: {
      profileId: string;
      payload?: { budget_minor?: number; max_attempts?: number };
    }) => charactersApi.generateAnchorPack(profileId, payload),
    onSuccess: (_data, { profileId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.characterAnchors(profileId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["character-profiles", profileId, "anchor-gate"],
      });
    },
  });
}

export function useUpdateAnchor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      anchorId,
      payload,
    }: {
      anchorId: string;
      payload: { selected?: boolean; qa_status?: string };
    }) => charactersApi.updateAnchor(anchorId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["character-anchors"] });
      void queryClient.invalidateQueries({ queryKey: ["character-profiles"] });
    },
  });
}

// --- Story Plans ---
export function useStoryPlans(configurationId: string) {
  return useQuery({
    queryKey: queryKeys.storyPlans(configurationId),
    queryFn: () => storyPlansApi.list(configurationId),
    enabled: !!configurationId,
  });
}

export function useStoryPlan(id: string) {
  return useQuery({
    queryKey: queryKeys.storyPlan(id),
    queryFn: () => storyPlansApi.get(id),
    enabled: !!id,
  });
}

export function useStoryPlanFull(id: string) {
  return useQuery({
    queryKey: queryKeys.storyPlanFull(id),
    queryFn: () => storyPlansApi.getFull(id),
    enabled: !!id,
  });
}

export function useGenerateStoryPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configurationId,
      payload,
    }: {
      configurationId: string;
      payload: import("./types").GenerateStoryPlanRequest;
    }) => storyPlansApi.generate(configurationId, payload),
    onSuccess: (_data, { configurationId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.storyPlans(configurationId),
      });
    },
  });
}

export function useUpdateScenePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storyPlanId,
      pageNumber,
      payload,
    }: {
      storyPlanId: string;
      pageNumber: number;
      payload: import("./types").UpdateScenePlanRequest;
    }) => storyPlansApi.updateScene(storyPlanId, pageNumber, payload),
    onSuccess: (_data, { storyPlanId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.storyPlanFull(storyPlanId),
      });
    },
  });
}

// --- Generation Runs ---
export function useGenerationRuns(
  configurationId: string,
  params: { status?: string; run_type?: string } = {},
) {
  return useQuery({
    queryKey: queryKeys.generationRuns(configurationId, params),
    queryFn: () => generationRunsApi.list(configurationId, params),
    enabled: !!configurationId,
  });
}

export function useCandidates(runId: string) {
  return useQuery({
    queryKey: queryKeys.candidates(runId),
    queryFn: () => generationRunsApi.listCandidates(runId),
    enabled: !!runId,
  });
}

export function useSelectCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      candidateId,
    }: {
      runId: string;
      candidateId: string;
    }) => generationRunsApi.selectCandidate(runId, candidateId),
    onSuccess: (_data, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.candidates(runId),
      });
    },
  });
}

// --- Assets ---
export function useAsset(id: string) {
  return useQuery({
    queryKey: queryKeys.asset(id),
    queryFn: () => assetsApi.get(id),
    enabled: !!id,
  });
}

// --- Book Versions ---
export function useBookVersions(configurationId: string) {
  return useQuery({
    queryKey: queryKeys.bookVersions(configurationId),
    queryFn: () => bookVersionsApi.list(configurationId),
    enabled: !!configurationId,
  });
}

export function useBookVersion(id: string) {
  return useQuery({
    queryKey: queryKeys.bookVersion(id),
    queryFn: () => bookVersionsApi.get(id),
    enabled: !!id,
  });
}

export function useApproveBookVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookVersionsApi.approve(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookVersion(id),
      });
      void queryClient.invalidateQueries({ queryKey: ["book-versions"] });
    },
  });
}

export function useBookVersionPages(id: string) {
  return useQuery({
    queryKey: ["book-versions", id, "pages"] as const,
    queryFn: () => bookVersionsApi.getPages(id),
    enabled: !!id,
  });
}

export function useBranchBookVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookVersionId,
      payload,
    }: {
      bookVersionId: string;
      payload?: import("./types").BranchBookVersionRequest;
    }) => bookVersionsApi.branch(bookVersionId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["configurations"] });
      void queryClient.invalidateQueries({ queryKey: ["book-versions"] });
    },
  });
}

export function useRenderedAssets(bookVersionId: string) {
  return useQuery({
    queryKey: queryKeys.renderedAssets(bookVersionId),
    queryFn: () => bookVersionsApi.listRenderedAssets(bookVersionId),
    enabled: !!bookVersionId,
  });
}

export function useCreateDownloadGrant() {
  return useMutation({
    mutationFn: ({
      bookVersionId,
      renderedAssetId,
      payload,
    }: {
      bookVersionId: string;
      renderedAssetId: string;
      payload: import("./types").CreateDownloadGrantRequest;
    }) =>
      bookVersionsApi.createDownloadGrant(
        bookVersionId,
        renderedAssetId,
        payload,
      ),
  });
}

// --- Jobs ---
export function useJobs(
  params: PageParams & { status?: string; job_type?: string } = {},
) {
  return useQuery({
    queryKey: queryKeys.jobs(params),
    queryFn: () => jobsApi.list(params),
  });
}

export function useConfigurationJobs(configurationId: string) {
  return useQuery({
    queryKey: queryKeys.configurationJobs(configurationId),
    queryFn: () => jobsApi.listForConfiguration(configurationId),
    enabled: !!configurationId,
  });
}
